import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { z } from 'zod';

import { matchDescriptorOption } from './matchDescriptorOption';
import {
  isIdentityDisplayType,
  resolveIdentityValue,
} from './resolveIdentityValue';
import type { ActiveFormDescriptorField, CoerceRejectReason } from './types';

// Strict parsing (below) needs the customParseFormat plugin; without it dayjs
// parses leniently and overflows invalid calendar dates (e.g. 2026-02-30 →
// 2026-03-02). Extension is idempotent.
dayjs.extend(customParseFormat);

export type CoerceResult =
  | { ok: true; value: unknown }
  | { ok: false; reason: CoerceRejectReason };

/**
 * Scalar display types coerced by a per-type zod schema below. Users, tags and
 * departments are NOT here: they are relational values resolved from a name to
 * a UUID-bearing shape, which is `resolveIdentityValue`'s job (AID-394).
 */
export const SUPPORTED_DISPLAY_TYPES = [
  'text',
  'textarea',
  'date',
  'number',
] as const;

/**
 * Display types whose legal values are a CLOSED SET, coerced by matching against
 * the field's resolved `options` rather than by a type schema (AID-393).
 *
 * AID-240 deferred these because `displayType: 'rating'` spans a numeric
 * taxonomy (`Priority` → `number`) and a string enum (`Status` →
 * `Action_Status_Enum`), indistinguishable from the form config, so coercing
 * blind would have written the wrong type. The resolved option set settles it:
 * each option carries its value in the field's own type, so the write is a
 * lookup rather than a conversion.
 *
 * A field of one of these types with no resolved `options` is `unsupported_type`,
 * NOT unconstrained — fail-closed, mirroring `buildActiveFormDescriptor`'s
 * treatment of untyped fields.
 */
export const CLOSED_SET_DISPLAY_TYPES = [
  'rating',
  'commonLookup',
  // Custom-attribute option fields. Not form-config display types — the
  // descriptor builder assigns these when it resolves a custom attribute's
  // option list from the org's schema.
  'select',
  'multiSelect',
] as const;

export const isClosedSetDisplayType = (displayType: string): boolean =>
  (CLOSED_SET_DISPLAY_TYPES as readonly string[]).includes(displayType);

// A string, or a primitive the model may send unquoted (number/boolean),
// stringified. Non-primitives (null/undefined/objects/arrays) fail the union.
const stringSchema = z
  .union([z.string(), z.number(), z.boolean()])
  .transform((value) => String(value));

// A date in `YYYY-MM-DD` (or its unpadded `YYYY-M-D` variant), normalised to the
// `YYYY-MM-DD` shape every date field's schema expects (see `StringDateSchema` in
// src/schemas/global.ts). Parsed STRICTLY so impossible calendar dates
// (2026-02-30) and non-date strings are rejected rather than overflowed; also
// rejects non-strings.
const DATE_INPUT_FORMATS = ['YYYY-MM-DD', 'YYYY-M-D'];
const dateSchema = z
  .string()
  .refine((value) => dayjs(value, DATE_INPUT_FORMATS, true).isValid())
  .transform((value) =>
    dayjs(value, DATE_INPUT_FORMATS, true).format('YYYY-MM-DD')
  );

// A finite number, or a non-empty numeric string. Empty strings, null and
// non-numeric strings are rejected (unlike a bare `z.coerce.number()`, which
// would turn `''`/`null` into 0).
const numberSchema = z
  .union([z.number(), z.string().trim().min(1)])
  .pipe(z.coerce.number())
  .refine((value) => Number.isFinite(value));

const SCHEMA_BY_DISPLAY_TYPE: Record<string, z.ZodType> = {
  text: stringSchema,
  textarea: stringSchema,
  date: dateSchema,
  number: numberSchema,
};

const coerceClosedSetValue = (
  field: ActiveFormDescriptorField,
  raw: unknown
): CoerceResult => {
  const options = field.options;
  // Fail-closed: a closed-set field whose options did not resolve is not
  // "anything goes". `buildActiveFormDescriptor` never publishes one, so this is
  // the independent guard rather than the expected path.
  if (!options || options.length === 0) {
    return { ok: false, reason: 'unsupported_type' };
  }

  if (field.multiple) {
    // A single proposed value is a one-element selection, not an error: the
    // proposal tool carries `value` as a string OR an array of strings, and the
    // common case for a multi-select is one value.
    const proposed = Array.isArray(raw) ? raw : [raw];
    if (proposed.length === 0) {
      return { ok: false, reason: 'invalid_value' };
    }

    const values: (boolean | number | string)[] = [];
    for (const candidate of proposed) {
      const option = matchDescriptorOption(options, candidate);
      // All or nothing. A partial write would silently drop part of the
      // selection the user is about to review, and the card would show a
      // selection they never agreed to.
      if (!option) {
        return { ok: false, reason: 'not_an_option' };
      }
      if (!values.includes(option.value)) {
        values.push(option.value);
      }
    }

    return { ok: true, value: values };
  }

  // A single-select given several values is rejected outright — there is no
  // non-arbitrary way to pick one of them.
  if (Array.isArray(raw)) {
    return { ok: false, reason: 'invalid_value' };
  }

  const option = matchDescriptorOption(options, raw);

  return option
    ? { ok: true, value: option.value }
    : { ok: false, reason: 'not_an_option' };
};

/**
 * Coerce a raw agent-proposed value into the shape the given field expects.
 *
 * Three paths, chosen by the field's `displayType`:
 *
 *  - CLOSED SET (`rating`, `commonLookup`, custom-attribute `select` /
 *    `multiSelect`): resolved against the field's own `options`, writing the
 *    matched option's value verbatim so a numeric taxonomy stays numeric and a
 *    string enum stays a string. No conversion happens at all.
 *  - IDENTITY (`users`, `tags`, `departments`): the proposed NAME is resolved
 *    against the field's browser-local identity vocabulary and wrapped in the
 *    UUID-bearing shape its schema expects. Nothing is published to the model
 *    for these, so the guard is resolution itself — exactly one match, or the
 *    field is rejected (AID-394).
 *  - SCALAR (`text`, `textarea`, `date`, `number`): a per-type zod schema
 *    (mirrors the `z.coerce` / `z.preprocess` coercion in the data-import
 *    package).
 *
 * Pure and side-effect free — the caller (`createApplyPrefill`) writes the
 * result. Rejects (never silently drops) unsupported types, unparseable values,
 * out-of-set values and unresolvable names so the UI can report exactly what did
 * not take.
 */
export const coerceFieldValue = (
  field: ActiveFormDescriptorField,
  raw: unknown
): CoerceResult => {
  if (isClosedSetDisplayType(field.displayType)) {
    return coerceClosedSetValue(field, raw);
  }

  if (isIdentityDisplayType(field.displayType)) {
    return resolveIdentityValue(field, raw);
  }

  const schema = SCHEMA_BY_DISPLAY_TYPE[field.displayType];
  if (!schema) {
    return { ok: false, reason: 'unsupported_type' };
  }

  const parsed = schema.safeParse(raw);

  return parsed.success
    ? { ok: true, value: parsed.data }
    : { ok: false, reason: 'invalid_value' };
};
